// Feature: MCRO001-F0023
// CI test: T-MCRO-023
import React, { useState } from 'react';
import { userApi } from '../../services/api';
import StepUpDialog from '../auth/StepUpDialog';
import { getStepUpChallengeFromError } from '../../utils/stepUp';

const payload = (response) => response?.data?.data || response?.data || {};
const formatDate = (value) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('en-GB');
};

export default function AccountOriginAudit({ userId, authorized = false }) {
  const [reason, setReason] = useState('');
  const [record, setRecord] = useState(null);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [stepUpChallenge, setStepUpChallenge] = useState(null);
  const [pendingReason, setPendingReason] = useState('');

  if (!authorized || !userId) return null;

  const inspectReason = async (inspectionReason) => {
    if (!inspectionReason) return;
    setLoading(true);
    setRecord(null);
    setStatus('');
    try {
      const response = payload(await userApi.inspectAccountOrigin(userId, inspectionReason));
      const value = response.origin || {};
      setRecord({
        userId: value.userId || 'Not recorded',
        accountStatus: value.accountStatus || 'Not recorded',
        creationChannel: value.creation?.channel || 'Not recorded',
        createdAt: value.creation?.createdAt,
        invitationId: value.creation?.invitationId || 'Not recorded',
        invitationRevision: value.creation?.invitationRevision ?? 'Not recorded',
        issuerUserId: value.creation?.issuerUserId || 'Not recorded',
        organisationId: value.creation?.organisationId || 'Not recorded',
        resourceType: value.creation?.resource?.type || 'Not recorded',
        resourceId: value.creation?.resource?.id || 'Not recorded',
        verificationMethod: value.creation?.verificationMethod || value.verification?.method || 'Not recorded',
        verifiedAt: value.verification?.verifiedAt,
        invitationRevokedAt: value.invitation?.revokedAt,
        grantId: value.grant?.id || 'None',
        permissionLevel: value.grant?.permissionLevel || 'None',
        grantCreatedAt: value.grant?.createdAt,
        grantRevokedAt: value.grant?.revokedAt,
        grantRevokedReason: value.grant?.revokedReason || 'None',
        correlationId: value.creation?.correlationId || 'Not recorded',
      });
      setStepUpChallenge(null);
      setPendingReason('');
      setStatus('Account-origin inspection recorded in the audit log.');
    } catch (error) {
      const challenge = getStepUpChallengeFromError(error);
      if (challenge?.id) {
        setPendingReason(inspectionReason);
        setStepUpChallenge(challenge);
        setStatus('A fresh two-step verification is required before this audited lookup can continue.');
      } else setStatus(error?.response?.status === 403
        ? 'You are not authorized to inspect account origin.'
        : 'Account-origin history is unavailable. The inspection attempt was recorded where supported.');
    } finally {
      setLoading(false);
    }
  };
  const inspect = async (event) => { event.preventDefault(); if (reason.trim().length >= 10) await inspectReason(reason.trim()); };

  return <section className="account-origin-audit" aria-labelledby="account-origin-title" data-testid="account-origin-audit">
    <h3 id="account-origin-title">Account origin</h3>
    <p className="subtitle">Read-only support evidence. Every inspection requires a reason and is audited.</p>
    <form onSubmit={inspect}>
      <label htmlFor={`account-origin-reason-${userId}`}>Inspection reason</label>
      <textarea id={`account-origin-reason-${userId}`} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Support case or reason for this lookup" />
      <button type="submit" className="btn btn-outline" disabled={loading || reason.trim().length < 10}>{loading ? 'Inspecting…' : 'Inspect account origin'}</button>
    </form>
    {status && <p role="status">{status}</p>}
    {record && <dl className="account-origin-record">
      <div><dt>Creation source</dt><dd>{record.creationChannel === 'resource_review_invitation' ? 'Sheet review invitation' : record.creationChannel}</dd></div>
      <div><dt>Account status</dt><dd>{record.accountStatus}</dd></div>
      <div><dt>Created</dt><dd>{formatDate(record.createdAt)}</dd></div>
      <div><dt>Email verified</dt><dd>{formatDate(record.verifiedAt)}</dd></div>
      <div><dt>Invitation</dt><dd>{record.invitationId} · revision {record.invitationRevision}</dd></div>
      <div><dt>Issuer user</dt><dd>{record.issuerUserId}</dd></div>
      <div><dt>Organisation</dt><dd>{record.organisationId}</dd></div>
      <div><dt>Resource</dt><dd>{record.resourceType} · {record.resourceId}</dd></div>
      <div><dt>Verification method</dt><dd>{record.verificationMethod}</dd></div>
      <div><dt>Invitation revoked</dt><dd>{formatDate(record.invitationRevokedAt)}</dd></div>
      <div><dt>Resource grant</dt><dd>{record.grantId} · {record.permissionLevel}</dd></div>
      <div><dt>Grant created</dt><dd>{formatDate(record.grantCreatedAt)}</dd></div>
      <div><dt>Grant revoked</dt><dd>{formatDate(record.grantRevokedAt)} · {record.grantRevokedReason}</dd></div>
      <div><dt>Correlation ID</dt><dd>{record.correlationId}</dd></div>
    </dl>}
    <StepUpDialog challenge={stepUpChallenge} title="Verify account-origin inspection" description="This audited identity-support lookup requires fresh two-step verification." onComplete={() => inspectReason(pendingReason)} onCancel={() => { setStepUpChallenge(null); setPendingReason(''); }} />
  </section>;
}
